import { useCallback, useEffect, useState } from 'react'
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, CardHeader, Divider, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import Loader from '../../common/Loader'
import MerchantSelect from './MerchantSelect'
import {
  SERVICE_TYPES, ZONES, listServiceTypes, createServiceType, updateServiceType, deleteServiceType,
  uploadServiceTypeCsv, downloadServiceTypeCsv, downloadServiceTypeTemplate, saveCsvBlob,
} from './serviceTypeConfigService'

const emptyForm = () => ({
  merchant: null, pickupPincode: '', zone: '', dropPincode: '',
  timeFrom: '', timeTo: '', serviceType: '', priority: '',
})

const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v)

export default function ServiceTypesTab() {
  const { enqueueSnackbar } = useSnackbar()
  const [merchantFilter, setMerchantFilter] = useState(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)

  // add/edit dialog
  const [dialog, setDialog] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)

  // csv upload
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadErrors, setUploadErrors] = useState(null)

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const load = useCallback(() => {
    setLoading(true)
    listServiceTypes(merchantFilter?.merchantId ?? null)
      .then((res) => setRows(res.data || []))
      .catch(() => enqueueSnackbar('Failed to load serviceType config', { variant: 'error' }))
      .finally(() => setLoading(false))
  }, [merchantFilter, enqueueSnackbar])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...emptyForm(), merchant: merchantFilter })
    setDialog(true)
  }

  const openEdit = (r) => {
    setEditingId(r.id)
    setForm({
      merchant: { id: r.merchantOid, merchantId: r.merchantId, name: `#${r.merchantId}` },
      pickupPincode: r.pickupPincode ?? '', zone: r.zone ?? '', dropPincode: r.dropPincode ?? '',
      timeFrom: r.timeFrom ?? '', timeTo: r.timeTo ?? '',
      serviceType: r.serviceType ?? '', priority: r.priority ?? '',
    })
    setDialog(true)
  }

  const submit = () => {
    if (form.merchant?.merchantId == null) {
      enqueueSnackbar('Select a merchant', { variant: 'warning' }); return
    }
    if (!form.serviceType) {
      enqueueSnackbar('Select a service type', { variant: 'warning' }); return
    }
    const num = (v) => (v === '' || v === null ? null : Number(v))
    const str = (v) => (v === '' || v === null ? null : String(v).trim())
    const body = {
      merchantId: form.merchant.merchantId,
      merchantOid: form.merchant.id || null,
      pickupPincode: str(form.pickupPincode),
      zone: str(form.zone),
      dropPincode: str(form.dropPincode),
      timeFrom: num(form.timeFrom),
      timeTo: num(form.timeTo),
      serviceType: form.serviceType,
      priority: num(form.priority),
    }
    setSaving(true)
    const call = editingId ? updateServiceType(editingId, body) : createServiceType(body)
    call
      .then(() => {
        enqueueSnackbar(editingId ? 'Config updated' : 'Config created', { variant: 'success' })
        setDialog(false); load()
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.error || 'Save failed', { variant: 'error' }))
      .finally(() => setSaving(false))
  }

  const remove = (r) => {
    if (!window.confirm(`Delete serviceType config #${r.id} (merchant ${r.merchantId} → ${r.serviceType})?`)) return
    deleteServiceType(r.id)
      .then(() => { enqueueSnackbar('Deleted', { variant: 'success' }); load() })
      .catch((err) => enqueueSnackbar(err?.response?.data?.error || 'Delete failed', { variant: 'error' }))
  }

  const doUpload = () => {
    if (!uploadFile) return
    setUploading(true); setUploadErrors(null)
    uploadServiceTypeCsv(uploadFile)
      .then(() => {
        enqueueSnackbar('CSV applied', { variant: 'success' })
        setUploadFile(null); load()
      })
      .catch((err) => {
        const errs = err?.response?.data?.errors
        if (Array.isArray(errs) && errs.length) {
          setUploadErrors(errs)
          enqueueSnackbar('CSV rejected — see row errors', { variant: 'warning' })
        } else {
          enqueueSnackbar(err?.response?.data?.error || 'Upload failed', { variant: 'error' })
        }
      })
      .finally(() => setUploading(false))
  }

  const doDownload = () => {
    downloadServiceTypeCsv(merchantFilter?.merchantId ?? null)
      .then((res) => saveCsvBlob(res,
        `merchant_service_type_config${merchantFilter?.merchantId ? `_${merchantFilter.merchantId}` : ''}.csv`))
      .catch(() => enqueueSnackbar('Download failed', { variant: 'error' }))
  }

  const doTemplate = () => {
    downloadServiceTypeTemplate()
      .then((res) => saveCsvBlob(res, 'merchant_service_type_config_template.csv'))
      .catch(() => enqueueSnackbar('Template download failed', { variant: 'error' }))
  }

  return (
    <Card>
      {(loading || saving) && <Loader />}
      <CardHeader
        title="Merchant service types"
        subheader="Assigns a shipment's allocationServiceType by (merchant × pickup pincode × zone|drop pincode × order-creation window). First match by priority wins; PACE stable-sorts couriers by the resolved tier."
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New rule</Button>}
      />
      <CardContent>
        {/* filter + CSV toolbar */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <MerchantSelect value={merchantFilter} onChange={setMerchantFilter}
              label="Filter by merchant (optional)" />
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button variant="outlined" component="label" size="small" startIcon={<UploadFileIcon />}>
                Choose CSV
                <input hidden type="file" accept=".csv"
                  onChange={(e) => { setUploadErrors(null); setUploadFile(e.target.files?.[0] || null) }} />
              </Button>
              {uploadFile && <Typography variant="caption">{uploadFile.name}</Typography>}
              <LoadingButton size="small" variant="contained" loading={uploading}
                disabled={!uploadFile} onClick={doUpload}>Upload</LoadingButton>
              <Divider orientation="vertical" flexItem />
              <Button variant="text" size="small" startIcon={<DownloadIcon />} onClick={doDownload}>
                Download CSV
              </Button>
              <Button variant="text" size="small" startIcon={<DescriptionIcon />} onClick={doTemplate}>
                Template
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {uploadErrors && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setUploadErrors(null)}>
            <AlertTitle>CSV rejected — nothing was saved. Fix these rows and re-upload:</AlertTitle>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {uploadErrors.map((e, i) => <li key={i}><Typography variant="caption">{e}</Typography></li>)}
            </Box>
          </Alert>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Merchant</TableCell>
              <TableCell>Pickup pincode</TableCell>
              <TableCell>Zone</TableCell>
              <TableCell>Drop pincode</TableCell>
              <TableCell>Time from</TableCell>
              <TableCell>Time to</TableCell>
              <TableCell>Service type</TableCell>
              <TableCell align="right">Priority</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.merchantId}</TableCell>
                <TableCell>{dash(r.pickupPincode)}</TableCell>
                <TableCell>{dash(r.zone)}</TableCell>
                <TableCell>{dash(r.dropPincode)}</TableCell>
                <TableCell>{dash(r.timeFrom)}</TableCell>
                <TableCell>{dash(r.timeTo)}</TableCell>
                <TableCell><Typography variant="body2" fontWeight={600}>{r.serviceType}</Typography></TableCell>
                <TableCell align="right">{r.priority}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="caption" color="text.secondary">No serviceType rules.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Edit serviceType rule' : 'New serviceType rule'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={6}>
              <MerchantSelect value={form.merchant} onChange={(m) => set('merchant', m)} label="Merchant *" />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth size="small" label="Service type *" value={form.serviceType}
                onChange={(e) => set('serviceType', e.target.value)}>
                {SERVICE_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" type="number" label="Priority"
                helperText="lower = first; default 100" value={form.priority}
                onChange={(e) => set('priority', e.target.value)} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Pickup pincode"
                helperText="warehouse; blank = any" value={form.pickupPincode}
                onChange={(e) => set('pickupPincode', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth size="small" label="Zone"
                helperText="preferred locality key; blank = any" value={form.zone}
                onChange={(e) => set('zone', e.target.value)}>
                <MenuItem value=""><em>— any —</em></MenuItem>
                {ZONES.map((z) => <MenuItem key={z} value={z}>{z}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Drop pincode"
                helperText="fallback when zone is null" value={form.dropPincode}
                onChange={(e) => set('dropPincode', e.target.value)} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label="Time from (HHmm)"
                helperText="e.g. 1700; blank = all day" value={form.timeFrom}
                onChange={(e) => set('timeFrom', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" type="number" label="Time to (HHmm)"
                helperText="e.g. 1100 (may wrap midnight)" value={form.timeTo}
                onChange={(e) => set('timeTo', e.target.value)} />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary">
            All match fields are optional — blank means “match anything”. An all-blank rule is the merchant default.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <LoadingButton variant="contained" loading={saving} onClick={submit}>
            {editingId ? 'Save changes' : 'Create rule'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
